<template>
  <div id="item">
    <div class="container" v-if="item">

      <ModifierNavigation />

      <div class="page-title">
        <span class="name">{{ item.name.fr }}</span>
        <button class="save btn btn-primary" @click="save" v-if="$store.getters.loggedUserCanSaveInventory">{{ $t("save")
        }}</button>
      </div>

      <Tabs>

        <TabSection :title="$t('modifiers.information')">
          <div class="columns">

            <div class="column">
              <div class="group">
                <LocalizedInput :title="$t('modifiers.name')" :translate="item.name" />
              </div>
              <div class="group">
                <LocalizedInput textarea :title="$t('modifiers.description')" :translate="item.description"
                  inputClass="editable-description" />
              </div>
              <div class="group">
                <LocalizedInput :max-size="20" :title="$t('items.kitchen_description')"
                  :translate="item.kitchen.description" />
              </div>
              <div class="group">
                <label>{{ $t('modifiers.is_removal') }}</label>
                <select v-model="item.removal">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
              <div class="group">
                <label>
                  <span>{{ $t('note') }} ({{ item.note ? item.note.length : 0 }}/{{ noteMaxLength }})</span>
                  <Tooltip :text="$t('note_tooltip')"></Tooltip>
                </label>
                <input type="text" v-model="item.note" :maxlength="noteMaxLength">
              </div>
            </div>

            <div class="column">
              <div class="group">
                <ImageUpload identifier="modifierImage" :object="item.image" @changed="item.image = $event"
                  :maxSizeInMb="0.8"></ImageUpload>
              </div>
            </div>

          </div>

          <InventoryUsagePanel v-if="$route.query.id" type="modifier" :id="$route.query.id"></InventoryUsagePanel>
        </TabSection>

        <TabSection :title="$t('items.payment_information')">
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('items.taxable') }}</label>
                <select v-model="item.taxable">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('items.quantifiable') }}</label>
                <select v-model="item.quantifiable">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('items.out_of_stock') }}</label>
                <select v-model="item.outOfStock" class="editable-outOfStock">
                  <option :value="true">{{ $t('yes') }}</option>
                  <option :value="false">{{ $t('no') }}</option>
                </select>
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>{{ $t('modifiers.external_id') }}</label>
                <ExternalIDInput
                  v-if="inventory"
                  v-model="item.externalId"
                  :inventory="inventory.id"
                  :sync-item="item"
                  input-class="editable-externalId"
                />
              </div>
              <div class="group" v-if="$store.getters.loggedUserHasSuperAdmin">
                <label>{{ $t('items.external_rules') }}</label>
                <input type="text" v-model="item.externalRules">
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('items.prices_configuration')">
          <ItemPrices :item="item" :validate="$validate"></ItemPrices>
        </TabSection>

        <TabSection :title="$t('modifiers.choices')">
          <p>{{ $t('modifiers.choices_description') }}</p>
          <ListMultiSelect :bank="choices" :bankTitle="$t('items.choice_bank')" :selected="item.formattedChoices"
            :selectedTitle="$t('items.selected_choices')" />
        </TabSection>

      </Tabs>

      <div class="footer-actions">
        <button class="btn btn-primary" @click="save" v-if="$store.getters.loggedUserCanModifyInventory">{{ $t("save")
        }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import ModifierNavigation from '@/components/modifiers/Navigation.vue'
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import ListMultiSelect from '@/components/tools/ListMultiSelect.vue';
  import ImageUpload from "@/components/tools/ImageUpload.vue";
  import ItemPrices from "@/components/inventory/ItemPrices";
  import ExternalIDInput from "@/components/inventory/ExternalIDInput";
  import {initPropertyOfMultiSelect, assignIdsOfMultiSelect} from "@/vueUtils";

  export default {

    name: 'ModifierEdit',

    components: { LocalizedInput, ImageUpload, ModifierNavigation, ListMultiSelect, ItemPrices, ExternalIDInput },

    data() {
      return {
        itemId: null,
        inventory: null,
        item: null,
        noteMaxLength: 16,
        choices: [],
        currencySymbol: '$'
      }
    },

    computed: {
      version() {
        return this.$store.state.contextVersion;
      },
      versionId() {
        return this.version ? this.version.id : null
      },
      inventoryReference() {
        return this.$store.state.contextCompany ? this.$store.state.contextCompany.nameCanonical : this.inventory.id;
      }
    },

    validate: {
      "item.name.fr": {
        required: false
      },
      "item.description.fr": {
        required: false
      },
      "item.priceSchemes": {
        array: true,
        elements: {
          unitPrice: {
            required: true,
            number: true,
            maxDecimals: 2
          }
        }
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "Menu";
      this.$store.state.pageIcon = "/icon/menu.svg";

      showSpinner();
      this.itemId = this.$route.query.id;
      this.inventory = await this.$store.getters.getContextInventory;
      this.currencySymbol = await this.$store.getters.getContextCurrencySymbol;
      this.choices = await this.getChoices({inventoryId: this.inventoryReference, augment: 'short'});

      if (this.itemId) {
        await this.loadItem();
      } else {
        let item = {
          name: { fr: "", en: "" },
          description: { fr: "", en: "" },
          externalId: null,
          image: null,
          removal: false,
          quantifiable: true,
          outOfStock: false,
          taxable: true,
          choices: [],
          note: undefined,
          priceSchemes: this.inventory.deliveryMethods.map(method => ({ method: method, unitPrice: null })),
          kitchen: {},
        };
        this.fixItemForForm(item);
        this.item = item;
      }
      hideSpinner();

      if (this.$store.getters.isBranchContextInventory) {
        window.lockFields(this.$store.getters.getOverloadableFields);
      } else if (!this.$store.getters.loggedUserCanModifyInventory) {
        window.lockFields();
      }
    },

    watch: {
      version: async function () {
        if (this.itemId) {
          showSpinner();
          await this.loadItem();
          hideSpinner();
        }
      },
      "$store.getters.isBranchContextInventory"(newValue) {
        if (newValue) {
          window.lockFields(this.$store.getters.getOverloadableFields);
        }
      }
    },

    methods: {

      ...mapActions(['getModifier', 'createModifier', 'saveModifier', 'saveImage', 'getImage', 'getChoices']),

      async loadItem() {
        let version = this.versionId
        let item = await this.getModifier({ itemId: this.itemId, inventoryId: this.inventoryReference, version });
        if (!item) {
          console.log("erreur de fetch item");
          toast(this.$t("error.no_server"), "error");
          return;
        }
        if (item.image) {
          item.image = await this.getImage({ inventoryId: this.inventoryReference, imageId: item.image });
        }
        this.fixItemForForm(item);
        this.item = item;
      },

      fixItemForForm(item) {
        item.externalRules = item.externalRules ? JSON.stringify(item.externalRules) : JSON.stringify({});
        item.kitchen = item.kitchen || {};
        item.kitchen.description = item.kitchen.description || { fr: "", en : "" };
        initPropertyOfMultiSelect(item, 'choices', 'formattedChoices', this.choices);
      },

      async save() {
        if (!this.$validate.valid()) {
          toast(this.$t("branch.error.required"), "warning");
          this.$validate.validateAllOnce();
          return;
        }

        showSpinner();
        let version = this.versionId || undefined;
        let data = await this.getSerializedDataAndExecutePreSaveTasks();
        let response = this.itemId
            ? await this.saveModifier({ data, version, inventoryId: this.inventoryReference, itemId: this.itemId })
            : await this.createModifier({ data, version, inventoryId: this.inventoryReference });
        hideSpinner();

        if (!response) {
          toast(this.$t("modifiers.error.save"), "error");
          return;
        }
        this.$router.push(this.$router.buildContext("/modifiers"));
      },

      async getSerializedDataAndExecutePreSaveTasks() {
        let data = this.getSerializedData();
        await this._formatImageBeforeSave(data);
        return data;
      },

      getSerializedData() {
        let data = JSON.parse(JSON.stringify(this.item));
        data.externalRules = data.externalRules ? JSON.parse(data.externalRules) : {};
        assignIdsOfMultiSelect(data, 'choices', 'formattedChoices');
        return data;
      },

      async _formatImageBeforeSave(obj) {
        if (obj.image && obj.image.url.startsWith("data:")) {
          let image = await this.saveImage({ inventoryId: this.inventoryReference, image: getImageData(obj.image) });
          obj.image = image.id;
        } else if (obj.image && obj.image.id) {
          obj.image = obj.image.id;
        } else if (obj.image && obj.image.deleted) {
          obj.image = null;
        }
        return obj;
      }
    },
  };
</script>

<style lang="scss" scoped>
  #item {
    display: flex;
  }
</style>